//
// Copyright (c) 2026 Comcast
//

import { createSlice, isAnyOf, isFulfilled } from '@reduxjs/toolkit'
import listenerMiddleware from 'listenerMiddleware'
import { navigate } from 'reducers/routing'
import debounce from 'lodash/debounce'
import { dismissConfirmation } from 'components/Shared/ConfirmDialog/Index'

import {
  restoreSession,
  authUser,
  signOutUser,
  updatePartnerView,
} from 'api/sessionUser'
import { fetchPartners } from 'api/partners'
import { fetchTicketingSystems } from 'api/ticketingSystems'

import * as sessionSelector from 'selectors/session'
import * as sessionUserSelector from 'selectors/sessionUser'

import { SESSION_TIMEOUT_SEC } from 'constants/constants'
import localStorageUtils from 'utils/localStorage'

const initialState = {
  userId: null,
  authPending: true,
  appLoading: true,
  // features for users who didn't sign in
  features: [],
}

export const sessionSlice = createSlice({
  name: 'session',
  initialState,

  reducers: {
    appIsLoaded: (state, action) => {
      state.appLoading = false
    },

    signOut: (state, action) => {
    },
  },

  extraReducers: (builder) => {
    builder

      // restoreSession and authUser requested
      .addMatcher(
        isAnyOf(
          restoreSession.matchPending,
          authUser.matchPending,
        ),
        (state) => {
          state.authPending = true
        }
      )

      // restoreSession and authUser received
      .addMatcher(
        isAnyOf(
          restoreSession.matchFulfilled,
          authUser.matchFulfilled,
        ),
        (state, { payload }) => {
          state.userId = payload.user?.id || null
          state.features = payload.features || []
          state.partners = payload.partners || []
          state.authPending = false
        }
      )

      // signOutUser received
      .addMatcher(
        signOutUser.matchFulfilled,
        (state, { payload }) => {
          state.userId = null
          state.authPending = false
          state.appLoading = false
          state.features = payload?.features || []
          state.partners = payload?.partners || []
        }
      )
  },
})


export const {
  appIsLoaded,
  signOut,
} = sessionSlice.actions

export default sessionSlice.reducer


const inAdvancePeriod = 60000
const resetSessionTimeout = SESSION_TIMEOUT_SEC * 1000 - inAdvancePeriod
const debouncedResetClientSession = debounce((listenerApi) => {
  const state = listenerApi.getState()
  const dispatch = listenerApi.dispatch

  const authenticated = sessionSelector.authenticated(state)
  if (!authenticated) {
    return
  }

  dispatch(signOutUser.initiate({ isSessionTimeout: true }))
  dismissConfirmation()
}, resetSessionTimeout)

const checkIfApiResponse = (action) => {
  const isRtkqResponse = isFulfilled(action) && !signOutUser.matchFulfilled(action)
  return isRtkqResponse
}

// Listeners

// preload app data
listenerMiddleware.startListening({
  matcher: isAnyOf(
    restoreSession.matchFulfilled,
    authUser.matchFulfilled,
  ),
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState()
    const dispatch = listenerApi.dispatch

    const authenticated = sessionSelector.authenticated(state)
    if (authenticated) {
      const userIsUserPrivileged = sessionUserSelector.isUserPrivileged(state)

      if (userIsUserPrivileged) {
        // Admin partners are preloaded for Main Menu
        await dispatch(fetchPartners.initiate())
      }
      // TODO: do not preload ticketing systems,
      // load them only from specific pages that need them
      await dispatch(fetchTicketingSystems.initiate())
    }
    dispatch(appIsLoaded())
  },
})

// App is loaded for the first time
listenerMiddleware.startListening({
  actionCreator: appIsLoaded,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState()

    // remember last logged in partner to set as a default in a Select
    const partnerId = sessionUserSelector.mainUserPartnerId(state)
    if (partnerId) {
      localStorage.setItem('last_signed_in_partner_id', partnerId)
    }
  },
})

// sign out
listenerMiddleware.startListening({
  actionCreator: signOut,
  effect: async (action, listenerApi) => {
    const dispatch = listenerApi.dispatch

    const redirectUrl = action?.payload.redirectUrl
    await dispatch(signOutUser.initiate())

    localStorageUtils.setLastApiResponseTime(null)

    if (redirectUrl) {
      dispatch(navigate(redirectUrl))
    }
  },
})

// on signOutUser API fulfilled
// here we get catch sign out with status 401 which miss partners and initiate
// restoreSession API request which will have partners and features
listenerMiddleware.startListening({
  matcher: signOutUser.matchFulfilled,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState()
    const dispatch = listenerApi.dispatch

    const authenticated = sessionSelector.authenticated(state)
    const isError401 = Boolean(action.payload?.error && action.payload?.message?.status === 401)
    if (!authenticated && isError401) {
      dispatch(restoreSession.initiate(
        { isSessionTimeout: true },
        { forceRefetch: true }
      ))
    }
  },
})

// we need to redirect here because redirection from NavPartners doesn't work when
// Router redirects from='/admin' to='/' and ticketing systems are not updated
listenerMiddleware.startListening({
  matcher: updatePartnerView.matchFulfilled,
  effect: async () => {
    window.location.href = '/'
  },
})

// listen API responses (except sign out)
listenerMiddleware.startListening({
  matcher: checkIfApiResponse,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState()
    const dispatch = listenerApi.dispatch

    const authenticated = sessionSelector.authenticated(state)
    if (!authenticated) {
      return
    }

    const isError401 = Boolean(action.payload?.error && action.payload?.message?.status === 401)
    if (isError401) {
      dispatch(restoreSession.initiate(
        undefined,
        { forceRefetch: true }
      ))
    } else {
      localStorageUtils.setLastApiResponseTime()
      debouncedResetClientSession(listenerApi)
    }
  },
})

// listen API responses on other tab
listenerMiddleware.startListening({
  type: localStorageUtils.LS_OTHER_TAB_API_RESPONSE,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState()
    const dispatch = listenerApi.dispatch

    const authenticated = sessionSelector.authenticated(state)
    if (!authenticated) {
      return
    }

    const wasSignOutOnOtherTab = Boolean(action.value === null)
    if (wasSignOutOnOtherTab) {
      dispatch(restoreSession.initiate(
        undefined,
        { forceRefetch: true }
      ))
      dismissConfirmation()
    } else {
      debouncedResetClientSession(listenerApi)
    }
  },
})
